import { useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { addDays, berlinInstant, todayKey } from '../../calendar/dates';
import { scheduleCase } from '../api';
import type { AfterSalesRecord } from '../types';
import styles from '../create/create.module.css';

export interface ScheduleDialogProps {
  record: AfterSalesRecord;
  onDone: () => Promise<void>;
  onClose: () => void;
}

/** „Termin eintragen …" (no frame; own texts): day and time of the craftsman's visit → `POST /after-sales/:id/schedule`. */
export function ScheduleDialog({ record, onDone, onClose }: ScheduleDialogProps) {
  const formId = useId();
  const [day, setDay] = useState(addDays(todayKey(), 1));
  const [time, setTime] = useState('09:00');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!day) next['day'] = 'Bitte ein Datum angeben.';
    if (!time) next['time'] = 'Bitte eine Uhrzeit angeben.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setPending(true);
    setBanner(null);
    try {
      await scheduleCase(record.id, berlinInstant(day, time));
      await onDone();
      onClose();
    } catch (error) {
      const field = error instanceof ApiError ? error.fields['appointmentAt'] : undefined;
      if (field) setErrors({ day: field });
      else setBanner(errorMessage(error));
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={520}
      eyebrow={record.numberLabel}
      title="Termin eintragen"
      subtitle="Der Vorgang wechselt zu „Termin“."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Termin eintragen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <div className={styles.row}>
          <TextField
            label="Datum"
            type="date"
            required
            value={day}
            onChange={(event) => setDay(event.target.value)}
            error={errors['day']}
          />
          <TextField
            label="Uhrzeit"
            type="time"
            required
            value={time}
            onChange={(event) => setTime(event.target.value)}
            error={errors['time']}
          />
        </div>
      </form>
    </Dialog>
  );
}
